import { FieldState, FormState } from "formstate";
import { action, makeObservable, observable } from "mobx";
import { PASSWORD_MIN_LENGTH } from "utils/forms/constants";
import {
  hasNumber,
  isEmail,
  minLength,
  required,
} from "utils/forms/validators";

type RegisterFormState = {
  email: FieldState<string>;
  password: FieldState<string>;
  repeatedPassword: FieldState<string>;
  firstName: FieldState<string>;
  lastName: FieldState<string>;
};

class RegisterFormUiStore {
  @observable
  registeredEmail!: string;
  @observable
  emailUnavailable!: boolean;

  constructor() {
    makeObservable(this);
  }

  email: FieldState<string> = new FieldState("").validators(
    required("register.email.error.required"),
    isEmail("register.email.error.invalidEmail"),
    this.isAvailableEmail("register.email.error.unavailable")
  );

  password: FieldState<string> = new FieldState("").validators(
    required("register.password.error.required"),
    minLength(PASSWORD_MIN_LENGTH, "register.password.error.minLength"),
    hasNumber("register.password.error.hasNumber")
  );

  repeatedPassword: FieldState<string> = new FieldState("").validators(
    required("register.repeatedPassword.error.required"),
    () => this.passwordsMustMatch()
  );
  firstName: FieldState<string> = new FieldState("").validators(
    required("register.firstName.error.required")
  );
  lastName: FieldState<string> = new FieldState("").validators(
    required("register.lastName.error.required")
  );

  form = new FormState<RegisterFormState>({
    email: this.email,
    password: this.password,
    repeatedPassword: this.repeatedPassword,
    firstName: this.firstName,
    lastName: this.lastName,
  });

  passwordsMustMatch() {
    return this.password.value === this.repeatedPassword.value
      ? false
      : "register.repeatedPassword.error.match";
  }

  isAvailableEmail(message: string) {
    return () => this.emailUnavailable && message;
  }

  @action
  setEmailUnavailable(value: boolean) {
    this.emailUnavailable = value;

    this.email.validate();
  }

  @action
  validateRepeatedPassword = async () => {
    if (!this.repeatedPassword.dirty) {
      return;
    }

    this.repeatedPassword.validate();
  };

  @action
  resetForm() {
    this.email.reset();
    this.password.reset();
    this.repeatedPassword.reset();
    this.firstName.reset();
    this.lastName.reset();
    this.emailUnavailable = false;
    this.registeredEmail = "";
  }
}

export default RegisterFormUiStore;
export const registerFormUiStore = new RegisterFormUiStore();
